import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmAction from '@/components/confirmaction';
import { Alert, Avatar, Button, Checkbox, Empty, Pagination, Select, Skeleton, Tag, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FeaturePage from '@/components/featurepage';
import PullToRefresh from '@/components/pulltorefresh';
import ApplicationActions from '@/components/applicationactions';
import { applicationColors } from '@/utils/applications';
import useViewedNotification from '@/utils/useViewedNotification';
import { fetchPostDetail, fetchApplications, reviewApplication, reviewApplications, fetchPostConversation, getPostErrorMessage } from '@/apis/posts';
import './index.scss';

const statusLabels = { pending: '待处理', approved: '已同意', rejected: '已拒绝', withdrawn: '已撤回' };
export const RecruitmentPanel = ({ postId, post, onChanged }) => {
  const location = useLocation();
  const targetId = new URLSearchParams(location.search).get('application');
  const [applications, setApplications] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(targetId ? '' : 'pending');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 480);
  const busyRef = useRef(false);
  const requestVersion = useRef(0);
  const locatedTarget = useRef(null);
  const targetRow = useRef(null);
  const navigate = useNavigate();
  const completed = Boolean(post.completed_at);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const loadApplications = useCallback(async () => {
    const version = ++requestVersion.current;
    const active = () => requestVersion.current === version;
    setLoading(true); setError(''); setSelected([]);
    try {
      const locating = targetId && locatedTarget.current !== targetId;
      let result = await fetchApplications(postId, { page: locating ? 1 : page, pageSize: 10, ...(!locating && status ? { status } : {}) });
      if (!Array.isArray(result?.data) || !result.pagination) throw new Error('申请列表响应无效');
      if (locating) {
        let targetPage = 1;
        while (active() && !result.data.some(item => String(item.id) === targetId) && targetPage < result.pagination.totalPages) {
          targetPage += 1;
          result = await fetchApplications(postId, { page: targetPage, pageSize: 10 });
        }
        if (active()) { locatedTarget.current = targetId; setPage(targetPage); setStatus(''); }
      }
      if (active()) { setApplications(result.data); setPagination(result.pagination); }
    } catch (err) {
      const text = getPostErrorMessage(err, '申请加载失败');
      if (active()) setError(text);
      throw new Error(text);
    } finally {
      if (active()) setLoading(false);
    }
  }, [postId, page, status, targetId]);
  useEffect(() => {
    void loadApplications().catch(() => {});
    return () => { requestVersion.current += 1; };
  }, [loadApplications, retry]);
  useEffect(() => {
    if (!loading && targetId) targetRow.current?.scrollIntoView?.({ block: 'center' });
  }, [loading, targetId, applications]);
  const decide = async (ids, approved, batch = false) => {
    if (busyRef.current || completed || ids.some(id => applications.find(item => item.id === id)?.status === 'withdrawn')) return;
    busyRef.current = true; setBusy(true);
    try {
      if (batch) await reviewApplications(postId, ids, approved);
      else await reviewApplication(postId, ids[0], approved);
      message.success(approved ? '已同意申请，申请人已加入帖子群聊' : '已拒绝申请');
      setRetry(value => value + 1); onChanged();
    } catch (err) { message.error(getPostErrorMessage(err, '审批失败')); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const openRoom = async () => {
    try { const room = await fetchPostConversation(postId); navigate(`/message/${room.id}`); }
    catch (err) {
      message.error(err?.response?.status === 404
        ? '帖子群聊尚未创建，或你还不是群成员'
        : getPostErrorMessage(err, '尚未生成帖子群聊，同意首位申请后即可进入'));
    }
  };
  return <PullToRefresh enabled={isMobile} disabled={loading || busy} onRefresh={loadApplications} successMessage="申请已刷新"><div className="postManage-recruitment">
    {completed && <Alert className="postManage-completed" type="info" showIcon title="活动已完成，参与名单已锁定" description="仍可查看申请记录和参与者的完成确认状态。" />}
    <div className="postManage-summary">
      <div className="postManage-progress"><span className="postManage-summary-label">招募进度</span><div className="postManage-progress-value"><strong>{post.approved_participant_count || 0}</strong><span>/ {post.recruitment_count || post.max_participants}</span></div><span className="postManage-summary-caption">已同意 / 招募人数</span></div>
      <Button onClick={openRoom}>进入帖子群聊</Button>
    </div>
    <div className="postManage-toolbar">
      <div className="postManage-filters">
        <Select aria-label="申请状态" value={status} disabled={busy} onChange={value => { setStatus(value); setPage(1); }} options={[{ value: '', label: '全部申请' }, ...Object.entries(statusLabels).map(([value, label]) => ({ value, label }))]} />
        {!isMobile && <Button disabled={busy || loading} onClick={() => setRetry(value => value + 1)}>刷新</Button>}
      </div>
      <div className="postManage-batch-actions">
        <Button type="primary" disabled={completed || !selected.length || busy || loading} loading={busy} onClick={() => decide(selected, true, true)}>同意所选 ({selected.length})</Button>
        <ConfirmAction title="确认拒绝所选申请？" onConfirm={() => decide(selected, false, true)} disabled={completed || !selected.length || busy || loading}><Button danger disabled={completed || !selected.length || busy || loading}>拒绝所选</Button></ConfirmAction>
      </div>
    </div>
    <div className="postManage-list-heading"><h2>申请记录</h2>{!loading && !error && pagination && <span>共 {pagination.total} 条</span>}</div>
    {loading ? <Skeleton active /> : error ? <Alert type="error" showIcon title={error} /> : applications.length === 0 ? <Empty description="暂无符合条件的申请" />
      : <div className="postManage-list">{applications.map(item => <article ref={String(item.id) === targetId ? targetRow : undefined} className={`postManage-application${String(item.id) === targetId ? ' postManage-application-target' : ''}`} key={item.id}>
        <div className="postManage-selection"><Checkbox disabled={completed || busy || item.status === 'withdrawn'} checked={selected.includes(item.id)} aria-label={`选择申请 ${item.id}`} onChange={event => setSelected(current => event.target.checked ? [...current, item.id] : current.filter(id => id !== item.id))} /></div>
        <div className="postManage-application-body">
          <div className="postManage-applicant">
            <Avatar size={40} src={item.applicant?.avatar || undefined} icon={<UserOutlined />} />
            <div className="postManage-identity">
              <div className="postManage-name"><strong>{item.applicant?.nickname || item.applicant?.username || '用户'}</strong><Tag className="postManage-status" color={applicationColors[item.status] || 'default'}>{statusLabels[item.status]}</Tag>{!item.comment_is_public && <Tag>私密申请</Tag>}{completed && item.status === 'approved' && <Tag className="postManage-completion" color={item.completion_confirmed_at ? 'cyan' : 'processing'}>{item.completion_confirmed_at ? '已确认完成' : '待确认完成'}</Tag>}{String(item.id) === targetId && <span className="postManage-target-label">当前定位</span>}</div>
              <div className="postManage-meta"><span>UID {item.applicant?.id}</span><time dateTime={item.created_at}>{new Date(item.created_at).toLocaleString('zh-CN')}</time></div>
            </div>
          </div>
          <p className="postManage-comment">{item.comment_content}</p>
          <div className="postManage-application-footer">
            <div className="postManage-review-actions"><Button type="primary" disabled={completed || busy || ['approved', 'withdrawn'].includes(item.status)} onClick={() => decide([item.id], true)}>同意</Button><ConfirmAction title="确认拒绝该申请？已加入的成员将从帖子群聊移除。" onConfirm={() => decide([item.id], false)} disabled={completed || busy || ['rejected', 'withdrawn'].includes(item.status)}><Button danger disabled={completed || busy || ['rejected', 'withdrawn'].includes(item.status)}>拒绝</Button></ConfirmAction></div>
            {item.actions && <ApplicationActions application={item} publisher onChanged={() => { setRetry(value => value + 1); onChanged(); }} />}
          </div>
        </div>
      </article>)}</div>}
    {pagination && <nav className="postManage-pagination" aria-label="申请列表分页"><Pagination current={page} pageSize={10} total={pagination.total} showSizeChanger={false} onChange={setPage} /></nav>}
  </div></PullToRefresh>;
};

const PostManage = () => {
  const location = useLocation();
  const { postId } = useParams();
  const { isAuthenticated, userInfo } = useSelector(state => state.user);
  const [post, setPost] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const reload = useCallback(() => setRetry(value => value + 1), []);
  const owner = post && userInfo?.id === post.user?.id;
  useViewedNotification(new URLSearchParams(location.search).get('notification'), Boolean(!loading && !error && owner));
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    setLoading(true); setError('');
    fetchPostDetail(postId).then(result => {
      if (!result?.id) throw new Error('帖子响应无效');
      if (active) setPost(result);
    }).catch(err => { if (active) setError(getPostErrorMessage(err, '帖子加载失败')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isAuthenticated, postId, retry]);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FeaturePage className="postManage" title="招募申请" back={`/posts/${postId}`} subtitle={post?.title} showDesktopBack={false}>
    {loading ? <Skeleton active /> : error ? <Alert type="error" showIcon title={error} action={<Button onClick={reload}>重试</Button>} />
      : !owner ? <Alert type="warning" showIcon title="仅帖子发布者可以管理招募申请" /> : <section className="featurePage-panel">
        <RecruitmentPanel postId={postId} post={post} onChanged={reload} />
      </section>}
  </FeaturePage>;
};
export default PostManage;
